import {
    useApi,
    Toaster,
    SubCategory,
    Category,
    CustomDataGrid,
    CloseIcon,
    DeleteIcon,
    Typography,
    Box
} from '../../../../utils/globalImports';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';

import {
    React,
    useState,
    useEffect,
    GridColDef,
    Tooltip,
    EditCalendarIcon,
    IconButton,
    Dialog,
    DialogTitle,
    Divider,
    DialogContent
} from '../../../../utils/globalImports';

import { ApiResponse } from '../../../../services/apiService';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    categoryData: Category;
    onRowEdit: (row: any) => void;  // Callback for editing a row
}

const ProjectSubCategory: React.FC<ModalProps> = ({ open, onClose, categoryData, onRowEdit }) => {

    const { execute, loading } = useApi();
    // State to manage the modal visibility

    const [subCategory, setSubCategory] = useState<any[]>([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState<any | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);  // State to manage confirm dialog "open/close" state
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });


    useEffect(() => {
        fetchSubCategories();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    // Fetch categories
    const fetchSubCategories = async () => {
        const { data } = await execute<SubCategory[]>('get', `/SubCategory/Category/${categoryData.id}`);
        if (data) {
            setSubCategory(data);
        }
    };

    const columns: GridColDef<(typeof subCategory)[number]>[] = [
        {
            field: 'description', headerName: 'Sub Category Name', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'notes', headerName: 'Notes', flex: 3, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'actions', headerName: 'Action', flex: 1, headerAlign: 'center',
            align: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Tooltip title="Edit Category"
                        sx={{
                            border: "2px solid #a3c63d",
                            borderRadius: "10px",
                            mr:1
                        }}
                    >
                        <IconButton
                            color="primary"
                            aria-label="edit"
                            onClick={() => onRowEdit(params.row)}
                        >
                            <EditCalendarIcon sx={{ color: '#a3c63d' }} ></EditCalendarIcon>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete"
                        sx={{
                            border: "2px solid #a3c63d",
                            borderRadius: "10px"
                        }}
                    >
                        <IconButton
                            color="primary"
                            aria-label="edit"
                            onClick={() => {
                                setSelectedSubCategory(params.row);
                                setDialogOpen(true);
                            }}
                        >
                            <DeleteIcon sx={{ color: '#a3c63d' }} ></DeleteIcon>
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const handleDialogConfirm = async () => {
        if (!selectedSubCategory) return;

        try {

            let response: ApiResponse<any> = await execute('delete', `/SubCategory/${selectedSubCategory.id}`);

            if (response!) {
                if (response.error) {
                    // If there's an error in the response, display the error message
                    setToast({ open: true, message: response.error, type: "error" });
                } else {
                    // If the response is successful, display the success message
                    setToast({ open: true, message: response.data, type: "success" });
                }
            }

            setSelectedSubCategory(null); // Clear selected row
            fetchSubCategories();
        }

        catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: '#1e66ab',
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'left',
                        alignItem: "left",
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '24px',
                    }}>
                    <Box
                        sx={{
                            display:"flex",
                            justifyContent:"center",
                            alignItems:"center",
                            gap:2,
                            mt:1
                        }}
                    >
                        <Box>
                            <FolderCopyOutlinedIcon 
                                sx={{
                                    fontSize:"30px",
                                    height:"55px",
                                    width:"55px",
                                    borderRadius:"50%",
                                    padding:0.9,
                                    backgroundColor:"#dde5f9"
                                }}
                            />
                        </Box>
                        <Box>
                            {categoryData.description}
                            <br/>
                            <Typography>
                                View and manage sub-categories
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton
                        color="inherit"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8, // Position the button at the right
                            top: 8,   // Position the button at the top
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider></Divider>

                <DialogContent>
                    <CustomDataGrid
                        customMessage='There are no sub-categories to display.'
                        rows={subCategory}
                        columns={columns}
                        isDialogOpen={dialogOpen}
                        actionName={'SubCategory'}
                        handleCancel={() => {
                            setSelectedSubCategory(null); // Reset selectedUser when modal closes
                            setDialogOpen(false);
                        }}
                        handleConfirm={handleDialogConfirm}
                    >
                    </CustomDataGrid>
                </DialogContent>


            </Dialog>

            {/* Toaster */}
            <Toaster
                open={toast.open}
                message={toast.message}
                type={toast.type as "success" | "error"}
                onClose={() =>
                    setToast({ ...toast, open: false })
                }
            />
        </>
    );
};

export default ProjectSubCategory;